<script setup lang="ts">
import { computed } from 'vue';
import { useArticleDetails } from '@/common/api/article.api';
import { useSelfServiceLinkCustomisation } from '../composables/useSelfServiceCustomisation';
import {
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  combineStyles,
} from '@/common/customisation/utils/styleUtils';
import { useRouter } from 'vue-router/composables';

interface Props {
  sectionKey: string;
}

const props = defineProps<Props>();

const router = useRouter();
const {
  isVisible,
  labelColor,
  backgroundColor,
  articleId: articleIdRef,
  borderColor,
} = useSelfServiceLinkCustomisation(props.sectionKey);

// Determine which article ID to use
const articleId = computed(() => {
  return articleIdRef.value as string | undefined;
});

// TanStack Query hook for fetching article details
// Uses the same query key as NewArticle.vue so they share the cache
const { data: articleData } = useArticleDetails(articleId);

const articleTitle = computed(() => articleData.value?.title || '');

const containerStyle = computed(() =>
  combineStyles(createBackgroundStyle(backgroundColor), createBorderStyle(borderColor))
);

const labelStyle = computed(() => createTextStyle(labelColor));

const showServiceLink = computed(() => {
  return isVisible.value && !!articleId.value;
});

const handleClick = () => {
  if (!articleId.value || !router) return;

  router.push({
    name: 'new-article',
    params: {
      id: articleId.value,
    },
  });
};
</script>

<template>
  <div
    v-if="showServiceLink && articleTitle"
    class="card-radius self-service-link"
    :style="containerStyle"
    tabindex="0"
    role="button"
    aria-label="Open article"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="link-content">
      <span class="link-text theme-font-main" :style="labelStyle">
        {{ articleTitle }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.self-service-link {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}

.link-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.link-text {
  flex: 1;
}
</style>
